import { DomSanitizer } from '@angular/platform-browser';
import { Subscription } from 'rxjs';
import { AnalyticsService, AnalyticsStatusSet } from './../../../services/analytics.service';
import { Component, Input, OnInit, Output, SimpleChanges, EventEmitter, OnChanges, OnDestroy, SecurityContext } from '@angular/core';
import { SapiService } from 'app/services/sapi.service';

interface BbFacetConfig {
	label?: string;
	name?: string;
}
interface BbFacetValueItem {
	count?: string;
	value?: any;
	label?: string;
	url?: string;
}
@Component({
	selector: 'bb-facet-filter',
	templateUrl: './bb-facet-filter.component.html',
	styleUrls: ['./bb-facet-filter.component.scss']
})
export class BbFacetFilterComponent implements OnInit, OnChanges, OnDestroy {
	@Input() facet: BbFacetConfig;
	@Input() facetData: BbFacetValueItem[];
	@Input() total: number;
	@Input() withHeading: boolean;

	@Output() filterChange: EventEmitter<void> = new EventEmitter();
	@Output() highlightChange: EventEmitter<void> = new EventEmitter();

	facetValueModels: {
		[facetValue: string]: {
			checked?: boolean;
			highlighted?: boolean;
		};
	} = {};
	analyticsServiceStatus: AnalyticsStatusSet;

	justSearched = false;
	collapsed = false;
	searchMode = false;
	waitForFacetData = false;
	facetFilters: string[] = [];
	facetValueHighlights: string[] = [];
	facetSearchTerm = '';
	hasQuerySet = false;

	subscriptions = new Subscription();

	constructor(
		private domSanitizer: DomSanitizer,
		private sapiService: SapiService,
		public analyticsService: AnalyticsService
	) { }

	ngOnInit(): void {
		this.subscriptions.add(this.analyticsService.statusSubject.subscribe((analyticsServiceStatus) => {
			this.analyticsServiceStatus = analyticsServiceStatus;
			this.facetFilters = (this.facet && this.facet.name && analyticsServiceStatus.filters[this.facet.name]) || [];
			this.facetValueHighlights = (this.facet && this.facet.name && analyticsServiceStatus.highlights[this.facet.name]) || [];
			if (!this.justSearched) {
				this.searchMode = false;
				this.facetSearchTerm = '';
			}
			this.justSearched = false;
			if (!this.facetData) {
				this.waitForFacetData = true;
			} else {
				this.onFacetStatusChange(this.facetFilters, this.facetValueHighlights);
			}
		}));
	}

	ngOnChanges(simpleChanges: SimpleChanges) {
		if (simpleChanges && simpleChanges.facetData && simpleChanges.facetData.currentValue && simpleChanges.facetData.currentValue.length) {
			this.createFacetValueModels();
		}
	}

	createFacetValueModels() {
		const showUrl = ['domain', 'rdomain', 'referrer', 'rreferrer'].includes(this.facet.name);
		this.facetData.forEach((facetValueItem) => {
			if (showUrl && facetValueItem.value) {
				const url = facetValueItem.value.includes('//') ? facetValueItem.value : 'https://' + facetValueItem.value;
				facetValueItem.url = this.domSanitizer.sanitize(SecurityContext.URL, url);
			}
			if (facetValueItem.value !== undefined && !this.facetValueModels[facetValueItem.value]) {
				this.facetValueModels[facetValueItem.value] = {};
			}
		});
		if (this.waitForFacetData) {
			this.waitForFacetData = false;
			this.onFacetStatusChange(this.facetFilters, this.facetValueHighlights);
		}
	}

	onFacetStatusChange(facetFilters, facetValueHighlights) {
		if (this.facetData && this.facetData.length) {
			this.getSolrQuerySet(this.facet.name);
			this.facetData.forEach((facetValueItem) => {
				if (facetValueItem.value !== undefined) {
					if (!this.facetValueModels[facetValueItem.value]) {
						this.facetValueModels[facetValueItem.value] = {};
					}
					this.facetValueModels[facetValueItem.value].checked = facetFilters.includes(facetValueItem.value);
					this.facetValueModels[facetValueItem.value].highlighted = facetValueHighlights.includes(facetValueItem.value);
				}
			});
		}
	}
	getSolrQuerySet(facetName: string) {
		this.sapiService.getUrl('/analytics/presets/').subscribe((data) => {
			this.hasQuerySet = false;
			if (data && data.items) {
				Object.keys(data.items).forEach((facet: any) => {
					const facetValue = data.items[facet];
					if (facetName === facet) {
						if (facetValue.solrQuerySet) {
							this.hasQuerySet = true;
						}
					}
				});
			}
		});
	}

	ngOnDestroy() {
		this.subscriptions.unsubscribe();
	}

	onExcludeToggleChange() {
		if (this.facetFilters && this.facetFilters.length) {
			this.analyticsService.update();
		}
	}

	onHighlightClick(facetItemValueIndex: number) {
		const facetValueItem = this.facetData[facetItemValueIndex];
		this.analyticsService.toggleFacetValueHighlight(this.facet.name, facetValueItem.value);
		this.highlightChange.emit();
	}

	onCheckboxChange(facetItemValueIndex: number, on: boolean) {
		this.facetSearchTerm = '';
		this.searchMode = false;
		const facetValueItem = this.facetData[facetItemValueIndex];
		if (on) {
			this.analyticsService.addFacetFilter(this.facet.name, facetValueItem.value);
		} else {
			this.analyticsService.removeFacetFilter(this.facet.name, facetValueItem.value);
		}
		this.filterChange.emit();
	}

	onFacetValueClick(facetItemValueIndex: number, facetValueModel: any) {
		this.facetSearchTerm = '';
		this.searchMode = false;
		const facetValueItem = this.facetData[facetItemValueIndex];
		if (!facetValueModel || !facetValueModel.checked) {
			this.analyticsService.addFacetFilter(this.facet.name, facetValueItem.value);
		} else {
			this.analyticsService.removeFacetFilter(this.facet.name, facetValueItem.value);
		}
		this.filterChange.emit();
	}

	onShowMoreClick() {
		if (this.facetSearchTerm) {
			this.justSearched = true;
		}
		const currentCount = this.analyticsService.status.activeFacets[this.facet.name];
		this.analyticsService.setFacetCount(this.facet.name, currentCount + 10, this.facetSearchTerm);
	}

	onShowLessClick() {
		if (this.facetSearchTerm) {
			this.justSearched = true;
		}
		this.analyticsService.setFacetCount(this.facet.name, 10, this.facetSearchTerm);
	}

	onSearchClick() {
		this.searchMode = true;
	}

	onFacetSearchTermChange() {
		if (this.facetSearchTerm === '') {
			this.searchMode = false;
		}
		const currentCount = this.analyticsService.status.activeFacets[this.facet.name];
		this.justSearched = true;
		this.analyticsService.setFacetCount(this.facet.name, currentCount, this.facetSearchTerm);
	}
}
